import React from "react";
import cx from "classnames";
import ReactModal from "react-modal";
import SpaceBetween from "../SpaceBetween";
import styles from "./Modal.module.scss";

export interface ModalHeaderProps {
  className?: string;
  onRequestClose?: ReactModal.Props["onRequestClose"];
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  className,
  children,
  onRequestClose,
}) => (
  <div className={cx(styles.header, className)}>
    <div className={styles.headerLeft}>
      <h1 className={styles.headerTitle}>{children}</h1>
    </div>
    {onRequestClose && (
      <button
        type="button"
        className={styles.headerClose}
        aria-label="Close Dialog"
        onClick={onRequestClose}
      >
        <span aria-hidden="true">&times;</span>
      </button>
    )}
  </div>
);

export interface ModalBodyProps {
  className?: string;
}

export const ModalBody: React.FC<ModalBodyProps> = ({ className, children }) => (
  <div className={cx(styles.body, className)}>{children}</div>
);

export interface ModalFooterProps {
  className?: string;
  start?: React.ReactNode;
  end?: React.ReactNode;
  spacing?: string;
  reverseSpacing?: string;
  verticalSpacing?: string;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  className,
  children,
  start,
  end,
  spacing,
  reverseSpacing,
  verticalSpacing,
}) => (
  <div className={cx(styles.footer, className)}>
    <SpaceBetween spacing={cx(styles.footerVerticalSpacing, verticalSpacing)}>
      {start && (
        <div className={styles.footerStart}>
          <SpaceBetween spacing={cx(styles.footerSpacing, spacing)}>{start}</SpaceBetween>
        </div>
      )}
      {children && (
        <div className={cx(styles.footerMiddle, { [styles.footerMiddleOnly]: !start && !end })}>
          <SpaceBetween spacing={cx(styles.footerSpacing, spacing)}>{children}</SpaceBetween>
        </div>
      )}
      {end && (
        <div className={cx(styles.footerEnd, { [styles.footerEndOnly]: !start && !children })}>
          <SpaceBetween spacing={cx(styles.footerReverseSpacing, reverseSpacing)}>
            {end}
          </SpaceBetween>
        </div>
      )}
    </SpaceBetween>
  </div>
);

export interface ModalProps extends ReactModal.Props {
  className?: string;
}

const Modal: React.FC<ModalProps> & { setAppElement(appElement: string | HTMLElement): void } = ({
  className,
  overlayClassName,
  children,
  ...otherProps
}) => {
  return (
    <ReactModal
      className={cx(styles.modal, className)}
      overlayClassName={cx(styles.overlay, overlayClassName)}
      {...otherProps}
    >
      {children}
    </ReactModal>
  );
};

Modal.setAppElement = ReactModal.setAppElement;

export default Modal;
